import { toFilterSelection } from '../../components/generics/Recommendations/useRecommendationsTab';
import { intlDateFormat } from '../../utils/dateUtils';
import { emptyArray } from '../../utils/arrayUtils';
import { ROUTE_LOCAL_VENDORS_DETAILS, ROUTE_VENDORS_DETAILS } from '../../constants';

export const V_TAB_VALUES = {
  SUMMARY: 1,
  CONTRACTS: 2,
  TEAMING: 3,
  NOTES: 4,
  GSA: 5,
};

export const V_TABS = [
  { id: 'summary', i18nKey: 'summary', value: V_TAB_VALUES.SUMMARY },
  { id: 'gsa', i18nKey: 'gsa', value: V_TAB_VALUES.GSA, requiresGsa: true },
  { id: 'contracts', i18nKey: 'contracts', value: V_TAB_VALUES.CONTRACTS },
  { id: 'teaming', i18nKey: 'teaming', value: V_TAB_VALUES.TEAMING },
  { id: 'notes', i18nKey: 'notes', value: V_TAB_VALUES.NOTES },
];

export function vendorHasGsaDetails(vendorInfo) {
  const gsaDetails = vendorInfo?.gsaDetails;
  return Boolean(gsaDetails?.hasGsaSchedule && !emptyArray(gsaDetails.gsaContracts));
}

export function getVendorDetailTabs(hasGsa) {
  return V_TABS.filter((tab) => !tab.requiresGsa || hasGsa);
}

export function getUniqueGsaSchedules(gsaDetails) {
  if (!gsaDetails) return [];
  const fromActive = Array.isArray(gsaDetails.gsaActiveSchedules) ? gsaDetails.gsaActiveSchedules : [];
  const fromContracts = (gsaDetails.gsaContracts || []).map((c) => c.schedule).filter(Boolean);
  return Array.from(new Set([...fromActive, ...fromContracts].filter(Boolean)));
}

export function resolveGsaSinDescription(sin, trans) {
  if (sin?.category) return sin.category;
  return typeof trans === 'function' ? trans('gsa-description-not-available') : 'Description not available';
}

export function getUniqueGsaSins(gsaDetails) {
  if (!gsaDetails) return [];
  const sinsMap = new Map();

  (gsaDetails.gsaContracts || []).forEach((contract) => {
    (contract.sins || []).forEach((sin) => {
      if (!sin?.code) return;
      const existing = sinsMap.get(sin.code) || {};
      sinsMap.set(sin.code, {
        code: sin.code,
        category: sin.category || existing.category || null,
        subcategory: sin.subcategory || existing.subcategory || null,
        coopPurchasing: Boolean(existing.coopPurchasing || sin.coopPurchasing),
        disasterRecovery: Boolean(existing.disasterRecovery || sin.disasterRecovery),
      });
    });
  });

  const topLevelCodes = Array.isArray(gsaDetails.gsaSins) ? gsaDetails.gsaSins : [];
  topLevelCodes.forEach((code) => {
    if (typeof code === 'string' && code && !sinsMap.has(code)) {
      sinsMap.set(code, { code, category: null });
    }
  });

  return Array.from(sinsMap.values());
}

export function getGsaPriceListUrl(contract) {
  if (!contract) return null;
  return contract.priceListUrl || contract.advantageUrl || null;
}

export function getGsaContractStatus(contract) {
  const isClosed = Boolean(contract?.closedForNewAwards);
  return {
    labelKey: isClosed ? 'gsa-closed-for-new-award' : 'gsa-open-for-new-award',
    isOpen: !isClosed,
  };
}

export const V_MANAGEMENT_DETAILS_TABS = [
  { id: 'summary', i18nKey: 'summary' },
  { id: 'notes', i18nKey: 'notes' },
];

export const V__CONTRACT_TABS = [
  { id: 'funding', i18nKey: 'funding' },
  { id: 'awards', i18nKey: 'awards' },
];

export function getContractAwardId(item) {
  return item && [item.referencedIDVId, item.awardContractId].filter((v) => !!v).join('-');
}

export function nameTypeContact(typeContact) {
  let type = 'Unrecognized';
  if (['governmentBusinessPOC', 'governmentBusinessAlternatePOC'].includes(typeContact)) type = 'Government';
  if (['electronicBusinessPOC', 'electronicBusinessAlternatePOC'].includes(typeContact)) type = 'Electronic';
  if (['pastPerformancePOC', 'pastPerformanceAlternatePOC'].includes(typeContact)) type = 'Performance';

  return type;
}

/** Re-enable year-award chips on /vendors list and quick view. */
export const SHOW_VENDOR_LIST_AWARDS = true;

export const generateLocationShort = (item) =>
  !item.city ? '-' : `${item.city}${item.country ? ', ' + item.country : ''}`;

export const generateLocation = (item) => {
  if (!item.city) return '';

  return [
    item.street || item.addressLine1,
    item.city,
    `${item.state || item.stateOrProvinceCode || ''} ${item.zipCode || ''}`.trim(),
    item.country || item.countryCode,
  ]
    .filter((val) => !!val)
    .join(', ');
};

export const orderOptions = [
  { name: 'RegistrationDate', value: 'registrationDate', asc: 'oldest-first', desc: 'newest-first' },
  { name: 'ActivationDate', value: 'activationDate', asc: 'oldest-first', desc: 'newest-first' },
  {
    name: 'current-year-awards',
    value: 'awardsCurrentYearAmount',
    desc: 'highest-first',
  },
  {
    name: 'previous-year-awards',
    value: 'awardsPreviousYearAmount',
    desc: 'highest-first',
  },
  {
    name: 'previous-5-years-awards',
    value: 'awardsPrevious5YearsAmount',
    desc: 'highest-first',
  },
  {
    name: 'previous-10-years-awards',
    value: 'awardsPrevious10YearsAmount',
    desc: 'highest-first',
  },
  { name: 'relevance', value: 'relevance', desc: 'most' },
];

export const localOrderOptions = [
  { name: 'RegistrationDate', value: 'registrationDate', asc: 'oldest-first', desc: 'newest-first' },
  { name: 'ActivationDate', value: 'activationDate', asc: 'oldest-first', desc: 'newest-first' },
  { name: 'relevance', value: 'relevance', desc: 'most' },
];

export const getRegistrationDate = (registrationDate) => {
  const now = new Date();
  const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

  return currentDate.toISOString() > registrationDate ? intlDateFormat(registrationDate) : null;
};

export const getVendorIdentifiers = (item) => {
  const { dunsCode, ueiCode, cageCode } = item;
  return [dunsCode, ueiCode, cageCode].filter((item) => !!item).join();
};

export const resolveVendorDetailPath = (item) => {
  if (item?.path) return item.path;
  const id = item?.id || item?.documentId || item?.record_id;
  if (!id) return '#';
  return item?.isLocalVendor
    ? ROUTE_LOCAL_VENDORS_DETAILS.path.replace(':id', id)
    : ROUTE_VENDORS_DETAILS.path.replace(':id', id);
};

export const buildRefinedVendorFilters = (criteria) => {
  const naics = toFilterSelection(criteria?.naics);
  const psc = toFilterSelection(criteria?.psc);
  const setAside = toFilterSelection(criteria?.setAsides);
  const state = toFilterSelection(criteria?.serviceRegions);
  const nextFilters = { reset: true, text: '' };
  if (naics) nextFilters.naicsCategory = naics;
  if (psc) nextFilters.pscCategory = psc;
  if (setAside) nextFilters.setAside = setAside;
  if (state) nextFilters.state = state;
  return nextFilters;
};
